import React, { useEffect } from 'react'
import { Button, Card, Col, Divider, InputNumber, Popover, Row, Spin, Steps, Table, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { AlertOutlined, CheckCircleOutlined, EditOutlined, ExclamationCircleOutlined, ReloadOutlined, SaveOutlined, PhoneOutlined, PhoneFilled, SmileFilled, FrownFilled, StopOutlined, StarFilled, LikeFilled, DislikeFilled } from '@ant-design/icons'
// eslint-disable-next-line import/no-cycle
import type { Moment } from 'moment'
// eslint-disable-next-line import/no-cycle
import { getPayslipForUser, openPayslipPdf, PayslipStatus } from '../../../reducers/payslips/actions'
// eslint-disable-next-line import/no-cycle
import { RootState } from '../../../reducers'
import PayslipInfoColumn from './PayslipInfoColumn'
import RatingsDetailDrawer from './RatingsDetailDrawer'
import TaskDetailRatingDrawer from './TaskDetailRatingDrawer'
import QrProblemDetailDrawer from './QrProblemDetailDrawer'
import SingleJobFinishDrawer from './SingleJobFinishDrawer'
// eslint-disable-next-line import/no-cycle
import MissingJobFieldsSummary from './MissingJobFieldsSummary'
// eslint-disable-next-line import/no-cycle
import JobDetailDrawer from './JobDetailDrawer'
import YearFinishesDrawer from './YearFinishesDrawer'
import ComplaintsDetailDrawer, { ComplaintStatus, IComplaintListItem } from './ComplaintsDetailDrawer'
import TelephoneSurveysDetailDrawer, { ITelephoneSurveyListItem } from './TelephoneSurveysDetailDrawer'
import ComplaintDetailDrawer from './ComplaintDetailDrawer'
import { COMPLAINT_STATUS, translateComplaintStatus } from '../../../reducers/complaints/actions'
import {
	TELEPHONE_SURVEY_CALL_STATUS,
	TELEPHONE_SURVEY_SATISFACTION,
	translateTelephoneSurveyCallStatus,
	translateTelephoneSurveySatisfaction
} from '../../../reducers/telephoneSurveys/actions'

const { Title, Text } = Typography

interface PayslipDrawerContentProps {
	userID: number
	selectedMonth: Moment // MMMM YYYY
	paycheckFromManager?: number | null
	setPaycheckFromManager: (value: number | null) => void
	isLoading: boolean
	onBlurSave: () => void
}

type ByStatusMap = Partial<Record<ComplaintStatus, number>>;

const PayslipDrawerContent: React.FC<PayslipDrawerContentProps> = ({
	userID,
	selectedMonth,
	paycheckFromManager,
	setPaycheckFromManager,
	isLoading,
	onBlurSave
}) => {
	const { t } = useTranslation('pages')
	const dispatch = useDispatch()

	const [isPayslipLoading, setIsPayslipLoading] = React.useState<boolean>(false)

	const payslip = useSelector((state: RootState) => state.payslips.payslipDetail.data)

	const [showRatingsDrawer, setShowRatingsDrawer] = React.useState<boolean>(false)
	const [ratingsDrawerData, setRatingsDrawerData] = React.useState<any>(null)

	const [showBadTasksDrawer, setShowBadTasksDrawer] = React.useState<boolean>(false)
	const [badTasksData, setBadTasksData] = React.useState<any[]>([])

	const [showGoodTasksDrawer, setShowGoodTasksDrawer] = React.useState<boolean>(false)
	const [goodTasksData, setGoodTasksData] = React.useState<any[]>([])

	const [showQrProblemDrawer, setShowQrProblemDrawer] = React.useState<boolean>(false)
	const [selectedJob, setSelectedJob] = React.useState<any | null>(null)

	const [selectedJobId, setSelectedJobId] = React.useState<number | null>(null)

	const [showYearFinishesDrawer, setShowYearFinishesDrawer] = React.useState(false)

	// +++ STAV PRE SŤAŽNOSTI
	const [showComplaintsDrawer, setShowComplaintsDrawer] = React.useState<boolean>(false)
	const [complaintsDrawerData, setComplaintsDrawerData] = React.useState<IComplaintListItem[]>([])
	const [selectedComplaintId, setSelectedComplaintId] = React.useState<number | null>(null)

	// +++ STAV PRE TELEFONICKÉ PRIESKUMY
	const [showSurveysDrawer, setShowSurveysDrawer] = React.useState<boolean>(false)
	const [surveysDrawerItems, setSurveysDrawerItems] = React.useState<ITelephoneSurveyListItem[]>([])
	const [surveysDrawerTitle, setSurveysDrawerTitle] = React.useState<string>('Telefonické prieskumy – detail')

	// helper na mapovanie
	const complaintsSummary = payslip?.complaints as
		| { byStatus?: ByStatusMap; total?: number; items?: IComplaintListItem[] }
		| undefined
	const byStatus: ByStatusMap = complaintsSummary?.byStatus ?? {}
	const telephoneSurveySummary = payslip?.telephoneSurveys as
		| {
			total?: number
			answeredCount?: number
			noAnswerCount?: number
			satisfiedCount?: number
			unsatisfiedCount?: number
			items?: ITelephoneSurveyListItem[]
		}
		| undefined

	const surveyItems: ITelephoneSurveyListItem[] = telephoneSurveySummary?.items || []

	const openSurveysDrawer = (filter: 'all' | 'answered' | 'no_answer' | 'satisfied' | 'unsatisfied') => {
		const filterMap: Record<string, ITelephoneSurveyListItem[]> = {
			all: surveyItems,
			answered: surveyItems.filter((s) => s.callStatus === 'ANSWERED'),
			no_answer: surveyItems.filter((s) => s.callStatus === 'NO_ANSWER'),
			satisfied: surveyItems.filter((s) => s.satisfaction === 'SATISFIED'),
			unsatisfied: surveyItems.filter((s) => s.satisfaction === 'UNSATISFIED')
		}
		const titleMap: Record<string, string> = {
			all: 'Všetky hovory',
			answered: 'Zdvihol',
			no_answer: 'Nedvíhal',
			satisfied: 'Spokojný',
			unsatisfied: 'Nespokojný'
		}
		setSurveysDrawerItems(filterMap[filter])
		setSurveysDrawerTitle(`Telefonické prieskumy – ${titleMap[filter]}`)
		setShowSurveysDrawer(true)
	}

	const complaintCounts = {
		unjustified: byStatus?.unjustified || 0,
		justified_unresolved: byStatus?.justified_unresolved || 0,
		justified_resolved_by_cleaner: byStatus?.justified_resolved_by_cleaner || 0,
		justified_resolved_by_manager: byStatus?.justified_resolved_by_manager || 0
	}

	const filterComplaints = (status?: ComplaintStatus) => {
		const all: IComplaintListItem[] = complaintsSummary?.items || []
		return status ? all.filter((x) => x.status === status) : all
	}

	const fetchPayslip = (forceRecalculate: boolean = false) => {
		if (userID) {
			setIsPayslipLoading(true)
			dispatch(getPayslipForUser(
				userID,
				selectedMonth.format('YYYY-MM'),
				forceRecalculate, // nový parameter
				(data) => {
					if (data) {
						setPaycheckFromManager(data.paycheckFromManager ?? null)
					}
					setIsPayslipLoading(false)
				}
			))
		}
	}

	useEffect(() => {
		fetchPayslip()
	}, [userID, selectedMonth.format('YYYY-MM')])

	const hasBonusData = (payslip?.chargedPriceForFlatsMap || []).some((row: any) => (row.bonusForYearFinishes || 0) > 0)

	const minPaycheckFromManager = payslip?.cleanedFlats && payslip?.cleanedFlats > 0 ? payslip?.cleanedFlats : 0
	const finalSalary = Number(payslip?.finalSalary || 0)

	const currentStep = {
		[PayslipStatus.DRAFT]: 0,
		[PayslipStatus.SAVED]: 1,
		[PayslipStatus.FINALIZED]: 2
	}[payslip?.status ?? PayslipStatus.DRAFT]

	// @ts-ignore
	return (
		<Spin spinning={isPayslipLoading || isLoading}>
			<div className="p-4">

				<Steps current={currentStep} direction="horizontal" size="small" className="mb-4">
					<Steps.Step title={t('pages:Návrh')} icon={<EditOutlined />} />
					<Steps.Step title={t('pages:Uložená')} icon={<SaveOutlined />} />
					<Steps.Step title={t('pages:Ukončená')} icon={<CheckCircleOutlined />} />
				</Steps>

				<Card
					className="mb-1 mt-8"
					bordered={false}
					style={{
						backgroundColor: '#f5f5f5',
						borderRadius: 12,
						padding: 0
					}}
				>
					<Row justify="space-between" align="middle">
						<Col>
							<Title level={5} style={{ marginBottom: 8 }}>
								{t('pages:Výplata')}
							</Title>
							<Text style={{ fontSize: 28, fontWeight: 700 }}>
								{finalSalary?.toFixed(2)} €
							</Text>
						</Col>

						<Col>
							<Row gutter={8}>
								<Col>
									<Button icon={<ReloadOutlined />} onClick={() => fetchPayslip(true)}>
										{t('pages:Prepočítať')}
									</Button>
								</Col>
								<Col>
									<Popover
										placement="top"
										content={() => {
											if (payslip?.status === PayslipStatus.DRAFT) {
												return t('pages:Najskôr je potrebné výplatnú pásku uložiť.')
											}
											if ((payslip?.errorMissingPriceCount ?? 0) > 0) {
												return t('pages:Chýbajú ceny za byty.')
											}
											if ((payslip?.errorMissingFlatsCount ?? 0) > 0) {
												return t('pages:Chýbajú počty bytov.')
											}
											if (!payslip?.payslipID) {
												return t('pages:Výplatná páska ešte nebola vytvorená.')
											}
											return null
										}}
									>
										<Button
											type="primary"
											disabled={
												(payslip?.status === PayslipStatus.DRAFT)
												|| (payslip?.errorMissingPriceCount ?? 0) > 0
												|| (payslip?.errorMissingFlatsCount ?? 0) > 0
												|| !payslip?.payslipID
											}
											onClick={() => {
												console.log('Generate PDF…')
												if (payslip?.payslipID) {
													openPayslipPdf(payslip.payslipID)
												}
											}}
										>
											PDF{' '}
											{(payslip?.status === PayslipStatus.DRAFT
												|| (payslip?.errorMissingPriceCount ?? 0) > 0
												|| (payslip?.errorMissingFlatsCount ?? 0) > 0
												|| !payslip?.payslipID) && (
												<span style={{ marginLeft: 6, color: 'orange', fontWeight: 700 }}>⚠</span>
											)}
										</Button>
									</Popover>
								</Col>

							</Row>
						</Col>
					</Row>
				</Card>
				<Divider />

				<Row gutter={24} className="mb-4">
					<Col span={8}>
						<Typography.Text type="secondary">{t('pages:Mzda')}</Typography.Text>
						<InputNumber
							value={paycheckFromManager || minPaycheckFromManager}
							onChange={(value) => {
								if (value !== undefined && value !== null && value >= minPaycheckFromManager) {
									setPaycheckFromManager(value)
								}
							}}
							// onBlur={onBlurSave}
							style={{ width: '100%' }}
							min={minPaycheckFromManager}
							max={payslip?.maximalBasePaycheck || Number.MAX_SAFE_INTEGER}
							step={1}
							precision={2}
						/>
					</Col>
				</Row>

				<Row gutter={24} className="mb-3">
					<PayslipInfoColumn
						title={t('pages:Základný plat')}
						value={payslip?.cleanedFlats != null ? `${payslip.cleanedFlats.toFixed(2)} €` : 'N/A'}
					/>
					<PayslipInfoColumn
						title={t('pages:Maximálny základný plat')}
						value={`${payslip?.maximalBasePaycheck?.toFixed(2)} €` || 'N/A'}
					/>
					<PayslipInfoColumn
						title={t('pages:Bonus ročné úlohy')}
						value={
							<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
								<Button
									type="link"
									style={{ padding: 0 }}
									onClick={() => setShowYearFinishesDrawer(true)}
								>
									{`${payslip?.annualCleaningBonus?.toFixed(2)} €` || 'N/A'}
								</Button>
								{(payslip?.errorMissingWindowsCount ?? 0) > 0 && (
									<Popover
										content={t('pages:Niektoré ročné upratovania nemajú vyplnený počet okien. Doplňte ho v zákazke.')}
									>
										<Button
											type="text"
											size="small"
											style={{ padding: 0, height: 'auto' }}
											onClick={() => setShowYearFinishesDrawer(true)}
											icon={<ExclamationCircleOutlined style={{ color: 'red', fontSize: 16 }} />}
										/>
									</Popover>
								)}
							</span>
						}
					/>
				</Row>

				<Divider />

				{payslip?.ratings && (
					<div className="mb-4">
						<Title level={5} style={{ marginBottom: 16 }}>
							{t('pages:Hodnotenia za')} <b>{selectedMonth.format('MM/YYYY')}</b>
						</Title>

						<Row gutter={16}>
							{(() => {
								const fourStarsCount = payslip?.ratings?.FOUR_STARS?.length ?? 0
								const threeStarsCount = payslip?.ratings?.THREE_STARS?.length ?? 0
								const twoStarsCount = payslip?.ratings?.TWO_STARS?.length ?? 0
								const oneStarCount = payslip?.ratings?.ONE_STAR?.length ?? 0
								const badTasksCount = payslip?.ratings?.BAD_TASKS?.reduce((sum, task) => sum + task.count, 0) ?? 0
								const goodTasksCount = payslip?.ratings?.GOOD_TASKS?.reduce((sum, task) => sum + task.count, 0) ?? 0

								const emptyStyle = { opacity: 0.4 }
								const starColor = (count: number, bad?: boolean) => (count > 0 && bad ? '#dc2626' : '#faad14')

								return (
									<>
										<Col>
											<Button
												type="default"
												disabled={fourStarsCount === 0}
												style={fourStarsCount === 0 ? emptyStyle : undefined}
												onClick={() => {
													setRatingsDrawerData(payslip?.ratings?.FOUR_STARS || [])
													setShowRatingsDrawer(true)
												}}
											>
												<StarFilled style={{ color: '#faad14' }} /> <StarFilled style={{ color: '#faad14' }} /> <StarFilled style={{ color: '#faad14' }} /> <StarFilled style={{ color: '#faad14' }} /> {fourStarsCount}
											</Button>
										</Col>
										<Col>
											<Button
												type="default"
												disabled={threeStarsCount === 0}
												style={threeStarsCount === 0 ? emptyStyle : undefined}
												onClick={() => {
													setRatingsDrawerData(payslip?.ratings?.THREE_STARS || [])
													setShowRatingsDrawer(true)
												}}
											>
												<StarFilled style={{ color: '#faad14' }} /> <StarFilled style={{ color: '#faad14' }} /> <StarFilled style={{ color: '#faad14' }} /> {threeStarsCount}
											</Button>
										</Col>
										<Col>
											<Button
												type="default"
												disabled={twoStarsCount === 0}
												style={twoStarsCount === 0 ? emptyStyle : undefined}
												onClick={() => {
													setRatingsDrawerData(payslip?.ratings?.TWO_STARS || [])
													setShowRatingsDrawer(true)
												}}
											>
												<StarFilled style={{ color: starColor(twoStarsCount, true) }} /> <StarFilled style={{ color: starColor(twoStarsCount, true) }} /> {twoStarsCount}
											</Button>
										</Col>
										<Col>
											<Button
												type="default"
												disabled={oneStarCount === 0}
												style={oneStarCount === 0 ? emptyStyle : undefined}
												onClick={() => {
													setRatingsDrawerData(payslip?.ratings?.ONE_STAR || [])
													setShowRatingsDrawer(true)
												}}
											>
												<StarFilled style={{ color: starColor(oneStarCount, true) }} /> {oneStarCount}
											</Button>
										</Col>
										<Col>
											<Button
												type="default"
												disabled={badTasksCount === 0}
												style={badTasksCount === 0 ? emptyStyle : undefined}
												onClick={() => {
													setBadTasksData(payslip?.ratings?.BAD_TASKS || [])
													setShowBadTasksDrawer(true)
												}}
											>
												<DislikeFilled style={{ color: '#dc2626' }} /> {badTasksCount}
											</Button>
										</Col>
										<Col>
											<Button
												type="default"
												disabled={goodTasksCount === 0}
												style={goodTasksCount === 0 ? emptyStyle : undefined}
												onClick={() => {
													setGoodTasksData(payslip?.ratings?.GOOD_TASKS || [])
													setShowGoodTasksDrawer(true)
												}}
											>
												<LikeFilled style={{ color: '#15803d' }} /> {goodTasksCount}
											</Button>
										</Col>
									</>
								)
							})()}

							{payslip?.errorQrCodesProblemCount > 0 && (
								<Col>
									<Button
										type="default"
										onClick={() => setShowQrProblemDrawer(true)}
										icon={<ExclamationCircleOutlined />}
									>
										{t('pages:QR problémy')} ({payslip.errorQrCodesProblemCount})
									</Button>
								</Col>
							)}
						</Row>

						{/* +++ NOVÁ ČASŤ – SŤAŽNOSTI */}
						{payslip?.complaints && (
							<>
								<Title level={5} style={{ margin: '12px 0 16px' }}>
									{t('pages:Reklamácie za')} <b>{selectedMonth.format('MM/YYYY')}</b>
								</Title>

								<Row gutter={12}>
									<Col>
										<Button
											danger={payslip.complaints.total > 0}
											onClick={() => {
												setComplaintsDrawerData(filterComplaints())
												setShowComplaintsDrawer(true)
											}}
											icon={<AlertOutlined />}
										>
											{t('pages:Všetky sťažnosti')} ({payslip.complaints.total})
										</Button>
									</Col>

									{complaintCounts.unjustified > 0 && (
										<Col>
											<Button
												onClick={() => {
													setComplaintsDrawerData(filterComplaints(COMPLAINT_STATUS.UNJUSTIFIED))
													setShowComplaintsDrawer(true)
												}}
											>
												{translateComplaintStatus(COMPLAINT_STATUS.UNJUSTIFIED, t)} ({complaintCounts.unjustified})
											</Button>
										</Col>
									)}

									{complaintCounts.justified_unresolved > 0 && (
										<Col>
											<Button
												type="primary"
												onClick={() => {
													setComplaintsDrawerData(filterComplaints(COMPLAINT_STATUS.JUSTIFIED_UNRESOLVED))
													setShowComplaintsDrawer(true)
												}}
											>
												{translateComplaintStatus(COMPLAINT_STATUS.JUSTIFIED_UNRESOLVED, t)} ({complaintCounts.justified_unresolved})
											</Button>
										</Col>
									)}

									{complaintCounts.justified_resolved_by_cleaner > 0 && (
										<Col>
											<Button
												onClick={() => {
													setComplaintsDrawerData(filterComplaints(COMPLAINT_STATUS.JUSTIFIED_RESOLVED_BY_CLEANER))
													setShowComplaintsDrawer(true)
												}}
											>
												{translateComplaintStatus(COMPLAINT_STATUS.JUSTIFIED_RESOLVED_BY_CLEANER, t)} ({complaintCounts.justified_resolved_by_cleaner})
											</Button>
										</Col>
									)}

									{complaintCounts.justified_resolved_by_manager > 0 && (
										<Col>
											<Button
												onClick={() => {
													setComplaintsDrawerData(filterComplaints(COMPLAINT_STATUS.JUSTIFIED_RESOLVED_BY_MANAGER))
													setShowComplaintsDrawer(true)
												}}
											>
												{translateComplaintStatus(COMPLAINT_STATUS.JUSTIFIED_RESOLVED_BY_MANAGER, t)} ({complaintCounts.justified_resolved_by_manager})
											</Button>
										</Col>
									)}
								</Row>
							</>
						)}
						{telephoneSurveySummary && (
							<>
								<Title level={5} style={{ margin: '12px 0 16px' }}>
									{t('pages:Telefonické prieskumy za')} <b>{selectedMonth.format('MM/YYYY')}</b>
								</Title>
								<Row gutter={12} wrap>
									<Col>
										<Button
											onClick={() => openSurveysDrawer('all')}
											icon={<PhoneOutlined style={{ color: '#6b7280' }} />}
										>
											{t('pages:Všetky hovory')} ({telephoneSurveySummary.total || 0})
										</Button>
									</Col>
									{(telephoneSurveySummary.answeredCount || 0) > 0 && (
										<Col>
											<Button
												onClick={() => openSurveysDrawer('answered')}
												icon={<PhoneFilled style={{ color: '#1d4ed8' }} />}
											>
												{translateTelephoneSurveyCallStatus(TELEPHONE_SURVEY_CALL_STATUS.ANSWERED, t)} ({telephoneSurveySummary.answeredCount})
											</Button>
										</Col>
									)}
									{(telephoneSurveySummary.noAnswerCount || 0) > 0 && (
										<Col>
											<Button
												onClick={() => openSurveysDrawer('no_answer')}
												icon={<StopOutlined style={{ color: '#c2410c' }} />}
											>
												{translateTelephoneSurveyCallStatus(TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER, t)} ({telephoneSurveySummary.noAnswerCount})
											</Button>
										</Col>
									)}
									{(telephoneSurveySummary.satisfiedCount || 0) > 0 && (
										<Col>
											<Button
												onClick={() => openSurveysDrawer('satisfied')}
												icon={<SmileFilled style={{ color: '#15803d' }} />}
											>
												{translateTelephoneSurveySatisfaction(TELEPHONE_SURVEY_SATISFACTION.SATISFIED, t)} ({telephoneSurveySummary.satisfiedCount})
											</Button>
										</Col>
									)}
									{(telephoneSurveySummary.unsatisfiedCount || 0) > 0 && (
										<Col>
											<Button
												danger
												onClick={() => openSurveysDrawer('unsatisfied')}
												icon={<FrownFilled style={{ color: '#dc2626' }} />}
											>
												{translateTelephoneSurveySatisfaction(TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED, t)} ({telephoneSurveySummary.unsatisfiedCount})
											</Button>
										</Col>
									)}
								</Row>
							</>
						)}

						<MissingJobFieldsSummary jobs={payslip?.chargedPriceForFlatsMap || []} />
					</div>

				)}

				<Table
					className="mt-3 mb-3"
					dataSource={payslip?.chargedPriceForFlatsMap || []}
					rowKey="jobId"
					pagination={false}
					size="small"
					bordered
					scroll={{ x: true }}
					columns={[
						{
							title: t('pages:Adresa'),
							dataIndex: 'address',
							key: 'address',
							// eslint-disable-next-line react/display-name
							render: (_: string, row: any) => (
								<Button type="link" onClick={() => setSelectedJobId(row.jobId)}>
									{row.address}
								</Button>
							)
						},
						{
							title: t('pages:Faktúra'),
							dataIndex: 'chargedPriceAll',
							key: 'chargedPriceAll',
							align: 'center',
							// eslint-disable-next-line react/display-name
							render: (chargedPriceAll: number, current) => {
								const maxPricePerJobAllowedToBePaid = chargedPriceAll * 0.26
								const jobHasLowPrice = current.flats > maxPricePerJobAllowedToBePaid

								const warningContent = (
									<div>
										<p style={{ color: 'red', marginBottom: 0 }}>
											{t('pages:Max povolená cena')}:{' '}
											<strong>{maxPricePerJobAllowedToBePaid.toFixed(2)} €</strong>
										</p>
										<p style={{ marginBottom: 0 }}>
											{t('pages:Malo by byť aspoň')}: <strong>{current.flats}</strong>
										</p>
									</div>
								)

								return (
									<Popover
										content={
											<div>
												<p>
													{t(
														'pages:Vyúčtovaná cena za celý vchod/ vyúčtovaná cena za zamestnanca. Ak zamestnanca niekto zaskakoval mohla nastať táto situácia'
													)}
												</p>
												{jobHasLowPrice && warningContent}
											</div>
										}
									>
										<div>
											<Text type="secondary">
												{chargedPriceAll?.toFixed(2)} €
											</Text>
											{jobHasLowPrice && (
												<span style={{ color: 'red', fontWeight: 700, marginLeft: 6 }}>⚠</span>
											)}
										</div>
									</Popover>
								)
							}
						},
						{
							title: t('pages:Cena za byt'),
							dataIndex: 'priceFlatPerMonth',
							key: 'priceFlatPerMonth',
							align: 'center',
							// eslint-disable-next-line react/display-name
							render: (price: number) => (price === 0 ? (
								<Popover content={t('pages:Cena za byt je povinný údaj. Treba ho doplniť.')}>
									<ExclamationCircleOutlined style={{ color: 'red', fontSize: 16 }} />
								</Popover>
							) : (
								<Typography.Text>{price?.toFixed(2)}</Typography.Text>
							))
						},
						{
							title: t('pages:Počet bytov'),
							dataIndex: 'flats',
							key: 'flats',
							align: 'center',
							// eslint-disable-next-line react/display-name
							render: (flats: number) => (flats === 0 ? (
								<Popover content={t('pages:Počet bytov je povinný údaj. Treba ho doplniť.')}>
									<ExclamationCircleOutlined style={{ color: 'red', fontSize: 16 }} />
								</Popover>
							) : (
								<Typography.Text>{flats}</Typography.Text>
							))
						},
						{
							title: t('pages:26% z faktúry'),
							dataIndex: 'twentySixPercentFromTotalInvoice',
							key: 'twentySixPercentFromTotalInvoice',
							align: 'center',
							// eslint-disable-next-line react/display-name
							render: (val: number) => (
								<Typography.Text>{val?.toFixed(2)} €</Typography.Text>
							)
						},
						{
							title: t('pages:Počet uprataní'),
							dataIndex: 'finishesOfUserCount',
							key: 'finishesOfUserCount',
							align: 'center',
							// eslint-disable-next-line react/display-name
							render: (count: number, row) => (
								<Button
									type="link"
									style={{ padding: 0 }}
									onClick={() => setSelectedJob(row)}
								>
									<span style={{ color: row.errorQrCodesProblemCount > 0 ? 'red' : undefined, fontWeight: row.errorQrCodesProblemCount > 0 ? 'bold' : 'normal' }}>{count}</span>
								</Button>
							)
						},
						{
							title: t('pages:Výplata základ'),
							dataIndex: 'baseSalaryForJob',
							key: 'baseSalaryForJob',
							align: 'center',
							// eslint-disable-next-line react/display-name
							render: (val: number) => (
								<span style={{
									color: val === 0 ? 'red' : undefined,
									fontWeight: val === 0 ? 'bold' : 'normal'
								}}>
									{val?.toFixed(2)} €
								</span>
							)
						},
						{
							title: t('pages:Výplata MAX'),
							dataIndex: 'twentySixPercentFromInvoice',
							key: 'twentySixPercentFromInvoice',
							align: 'center',
							// eslint-disable-next-line react/display-name
							render: (val: number) => (
								<Typography.Text>{val?.toFixed(2)} €</Typography.Text>
							)
						},
						...(hasBonusData ? [{
							title: t('pages:Bonus'),
							dataIndex: 'bonusForYearFinishes',
							key: 'bonusForYearFinishes',
							align: 'center' as const,
							// eslint-disable-next-line react/display-name
							render: (bonus: number) => (
								<span style={{
									color: bonus === 0 ? 'red' : undefined,
									fontWeight: bonus === 0 ? 'bold' : 'normal'
								}}>
									{bonus?.toFixed(2)} €
								</span>
							)
						}] : []),
						{
							title: t('pages:Status'),
							dataIndex: 'priceFlatPerMonthMissingError',
							key: 'priceFlatPerMonthMissingError',
							align: 'center',
							width: 80,
							// eslint-disable-next-line react/display-name
							render: (_: boolean, data) => {
								const missingFlat = data.missingFlatCountError
								const missingPrice = data.priceFlatPerMonthMissingError

								if (missingFlat || missingPrice) {
									let tooltipContent = ''
									if (missingFlat && missingPrice) {
										tooltipContent = t('pages:Chýba počet bytov a aj cena za byt.')
									} else if (missingFlat) {
										tooltipContent = t('pages:Táto zákazka nemá vyplnený počet bytov.')
									} else if (missingPrice) {
										tooltipContent = t('pages:Táto zákazka nemá vyplnenú cenu za byt.')
									}

									return (
										<Popover content={tooltipContent}>
											<ExclamationCircleOutlined style={{ color: 'orange', fontSize: 16 }} />
										</Popover>
									)
								}

								return <CheckCircleOutlined style={{ color: 'green', fontSize: 16 }} />
							}
						}

					]}
					summary={(pageData) => {
						const totalFlats = pageData.reduce((sum, row) => sum + (row.flats || 0), 0)
						const totalBonus = pageData.reduce((sum, row) => sum + (row.bonusForYearFinishes || 0), 0)
						const invoicesSum = pageData.reduce((sum, row) => sum + ((row as any).chargedPriceAll || 0), 0)
						const totalBaseSalary = pageData.reduce((sum, row) => sum + (row.baseSalaryForJob || 0), 0)
						const totalVyplataMax = pageData.reduce((sum, row) => sum + (row.twentySixPercentFromInvoice || 0), 0)

						return (
							<Table.Summary.Row>
								<Table.Summary.Cell index={0}><strong>{t('pages:Súčet')}</strong></Table.Summary.Cell>
								<Table.Summary.Cell index={1} align="center"><strong>{invoicesSum.toFixed(2)} €</strong></Table.Summary.Cell>
								<Table.Summary.Cell index={2} />
								<Table.Summary.Cell index={3} align="center"><strong>{totalFlats}</strong></Table.Summary.Cell>
								<Table.Summary.Cell index={4} />
								<Table.Summary.Cell index={5} />
								<Table.Summary.Cell index={6} align="center"><strong>{totalBaseSalary.toFixed(2)} €</strong></Table.Summary.Cell>
								<Table.Summary.Cell index={7} align="center"><strong>{totalVyplataMax.toFixed(2)} €</strong></Table.Summary.Cell>
								{hasBonusData && <Table.Summary.Cell index={8} align="center"><strong>{totalBonus.toFixed(2)} €</strong></Table.Summary.Cell>}
								<Table.Summary.Cell index={hasBonusData ? 9 : 8} />
							</Table.Summary.Row>
						)
					}}
				/>

				<ComplaintsDetailDrawer
					items={complaintsDrawerData}
					open={showComplaintsDrawer}
					onClose={() => setShowComplaintsDrawer(false)}
					onOpenComplaint={(id) => setSelectedComplaintId(id)}
				/>

				<TelephoneSurveysDetailDrawer
					items={surveysDrawerItems}
					open={showSurveysDrawer}
					onClose={() => setShowSurveysDrawer(false)}
					title={surveysDrawerTitle}
				/>

				{selectedComplaintId && (
					<ComplaintDetailDrawer
						complaintId={selectedComplaintId}
						open={!!selectedComplaintId}
						onClose={() => setSelectedComplaintId(null)}
					/>
				)}

				<RatingsDetailDrawer
					ratings={ratingsDrawerData}
					open={showRatingsDrawer}
					onClose={() => setShowRatingsDrawer(false)}
				/>

				<TaskDetailRatingDrawer
					tasks={badTasksData}
					open={showBadTasksDrawer}
					onClose={() => setShowBadTasksDrawer(false)}
				/>

				<TaskDetailRatingDrawer
					tasks={goodTasksData}
					open={showGoodTasksDrawer}
					onClose={() => setShowGoodTasksDrawer(false)}
					isGood
				/>

				<QrProblemDetailDrawer
					items={payslip?.chargedPriceForFlatsMap || []}
					open={showQrProblemDrawer}
					onClose={() => setShowQrProblemDrawer(false)}
				/>
				{selectedJob && (
					<SingleJobFinishDrawer
						job={selectedJob}
						open={!!selectedJob}
						onClose={() => setSelectedJob(null)}
					/>
				)}
				{selectedJobId && (
					<JobDetailDrawer
						jobId={selectedJobId}
						open={!!selectedJobId}
						onClose={() => setSelectedJobId(null)}
					/>
				)}

				<YearFinishesDrawer
					open={showYearFinishesDrawer}
					onClose={() => setShowYearFinishesDrawer(false)}
					finishes={payslip?.allYearFinishes || []}
					onOpenJob={(jobId) => {
						setShowYearFinishesDrawer(false)
						setSelectedJobId(jobId)
					}}
				/>

				{/* <pre style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}> */}
				{/* 	{JSON.stringify(payslip, null, 2)} */}
				{/* </pre> */}
			</div>
		</Spin>
	)
}

export default PayslipDrawerContent
